import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";

const HIDDEN_RESOURCE_TYPES = new Set(["Patient", "DocumentReference"]);

const RESOURCE_META: Record<string, { label: string; icon: string }> = {
  Practitioner: { label: "Practitioners", icon: "lucide:stethoscope" },
  Encounter: { label: "Encounters", icon: "lucide:calendar-clock" },
  Condition: { label: "Conditions", icon: "lucide:heart-pulse" },
  MedicationRequest: { label: "Medication Requests", icon: "lucide:pill" },
  AllergyIntolerance: { label: "Allergies", icon: "lucide:shield-alert" },
  Observation: { label: "Observations", icon: "lucide:activity" },
  DiagnosticReport: { label: "Diagnostic Reports", icon: "lucide:file-chart-column" },
  Procedure: { label: "Procedures", icon: "lucide:stethoscope" },
  Immunization: { label: "Immunizations", icon: "lucide:syringe" },
  CarePlan: { label: "Care Plans", icon: "lucide:clipboard-list" },
  FamilyMemberHistory: { label: "Family Member Histories", icon: "lucide:users" },
  Coverage: { label: "Coverages", icon: "lucide:shield-check" },
  ServiceRequest: { label: "Service Requests", icon: "lucide:clipboard-plus" },
};

function humanizeResourceType(type: string): string {
  return type.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

export function visibleFields(fields: Record<string, unknown>): [string, unknown][] {
  return Object.entries(fields).filter(
    ([key, value]) => key !== "patient_ref" && value !== null && value !== undefined && value !== "",
  );
}

export function groupClinicalRecords(records: ParsedRecord[]) {
  const byType = new Map<string, ParsedRecord[]>();
  for (const record of records) {
    if (HIDDEN_RESOURCE_TYPES.has(record.resourceType)) continue;
    const items = byType.get(record.resourceType) ?? [];
    items.push(record);
    byType.set(record.resourceType, items);
  }
  return [...byType.entries()]
    .map(([type, items]) => ({
      type,
      items,
      meta: RESOURCE_META[type] ?? { label: humanizeResourceType(type), icon: "lucide:file-heart" },
    }))
    .sort((a, b) => a.meta.label.localeCompare(b.meta.label));
}

/** The preview's records plus each type's "Show more" pages, each record once. */
export function withMoreRecords(records: ParsedRecord[], moreByType: Record<string, ParsedRecord[]>): ParsedRecord[] {
  const seen = new Set(records.map((r) => r.id));
  const out = [...records];
  for (const more of Object.values(moreByType)) {
    for (const record of more) {
      if (!seen.has(record.id)) {
        seen.add(record.id);
        out.push(record);
      }
    }
  }
  return out;
}

/**
 * Each group's true size in the whole upload. The records are one page,
 * newest first, so a large upload's page holds only some of each type (and
 * a type can be missing from it entirely); `total` is the server's count, or
 * the page's own count when the server sent none.
 */
export function withTypeTotals(
  groups: ReturnType<typeof groupClinicalRecords>,
  typeCounts: Record<string, number> | null | undefined,
) {
  const out = groups.map((group) => ({ ...group, total: Math.max(typeCounts?.[group.type] ?? 0, group.items.length) }));
  for (const [type, total] of Object.entries(typeCounts ?? {})) {
    if (total > 0 && !HIDDEN_RESOURCE_TYPES.has(type) && !out.some((g) => g.type === type)) {
      out.push({
        type,
        items: [],
        meta: RESOURCE_META[type] ?? { label: humanizeResourceType(type), icon: "lucide:file-heart" },
        total,
      });
    }
  }
  return out.sort((a, b) => a.meta.label.localeCompare(b.meta.label));
}

export function collapsePractitionerDuplicates(records: ParsedRecord[]) {
  const collapsed = new Map<string, { record: ParsedRecord; duplicateCount: number }>();
  for (const record of records) {
    const signature = JSON.stringify(visibleFields(record.fields).sort(([a], [b]) => a.localeCompare(b)));
    const existing = collapsed.get(signature);
    if (existing) existing.duplicateCount += 1;
    else collapsed.set(signature, { record, duplicateCount: 1 });
  }
  return [...collapsed.values()];
}

// "birth_date" -> "Birth Date" — the raw field names are internal column names,
// not written for a screen, so they get title-cased rather than shown as-is.
export function formatLabel(key: string): string {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

// "image/jpeg" -> "Image", "application/pdf" -> "PDF" — content_type is the only
// reliably-populated field on a directly-uploaded document's DocumentReference
// (see backend's _get_or_create_document_reference), so it's the only thing this
// can key off besides the parent upload's own file name.
export function documentKind(contentType: unknown): string {
  if (typeof contentType !== "string") return "Document";
  if (contentType.startsWith("image/")) return "Image";
  if (contentType === "application/pdf") return "PDF";
  return "Document";
}

export function documentLabel(record: ParsedRecord, index: number, total: number, fallbackFileName?: string): string {
  if (total === 1 && fallbackFileName) return fallbackFileName;
  const kind = documentKind(record.fields.content_type);
  return total > 1 ? `${kind} ${index + 1}` : kind;
}

/** A DocumentReference row can come from two different sources that share this one
 *  resource type: the vendor export's own FHIR bulk-export metadata (a genuine
 *  DocumentReference.ndjson/tsv entry — describes a document but was never an
 *  embedded file in this upload, so it has no pages to show), or a real file
 *  document_extraction_service.py found and actually extracted.
 *  has_extracted_content (set by the backend from whether a
 *  document_extraction_status row exists — see patient_records/queries.py) is the
 *  only reliable way to tell them apart. */
export function extractedDocumentRecords(records: ParsedRecord[]): ParsedRecord[] {
  return records.filter((r) => r.resourceType === "DocumentReference" && r.fields.has_extracted_content === true);
}
